import { Link } from 'react-router-dom'
import ServiceCard from '../components/ServiceCard'
import ComplianceGap from '../components/ComplianceGap'
import CTASection from '../components/CTASection'

const Home = () => {
  const services = [
    {
      id: 'tank-inspections',
      title: 'Annual Tank Inspections',
      description: 'Comprehensive inspections of underground and aboveground storage tanks to ensure compliance with state and federal regulations.',
      icon: 'clipboard-check',
      price: '',
      link: '/services#tank-inspections'
    },
    {
      id: 'monthly-walkthroughs',
      title: 'Monthly Walkthroughs',
      description: 'Regular facility inspections to identify and address potential compliance issues before they become problems.',
      icon: 'calendar',
      price: '',
      link: '/services#monthly-walkthroughs'
    },
    {
      id: 'hydrostatic-testing',
      title: 'Hydrostatic Testing',
      description: 'Testing of UST sumps and spill buckets to verify integrity and prevent environmental contamination.',
      icon: 'beaker',
      price: '',
      link: '/services#hydrostatic-testing'
    },
    {
      id: 'compliance-audits',
      title: 'Compliance Assistance',
      description: 'In-depth evaluation of your facility\'s compliance status with recommendations prior to state inspections.',
      icon: 'document-search',
      price: '',
      link: '/services#compliance-audits'
    }
  ]

  return (
    <>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-dark via-primary to-primary-light text-white py-24 relative overflow-hidden">
        {/* Subtle SVG background — underground tank system blueprint */}
        <div className="absolute inset-0 opacity-[0.06]">
          <svg className="w-full h-full" viewBox="0 0 1200 600" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice">
            {/* Ground line */}
            <line x1="0" y1="260" x2="1200" y2="260" stroke="white" strokeWidth="2" strokeDasharray="8 4" />

            {/* Underground tank 1 */}
            <rect x="120" y="300" width="280" height="140" rx="70" stroke="white" strokeWidth="2.5" fill="none" />
            <rect x="135" y="315" width="250" height="110" rx="55" stroke="white" strokeWidth="1" fill="none" strokeDasharray="4 3" />
            {/* Tank fill pipes */}
            <line x1="200" y1="260" x2="200" y2="300" stroke="white" strokeWidth="2" />
            <line x1="320" y1="260" x2="320" y2="300" stroke="white" strokeWidth="2" />
            {/* Fill pipe caps */}
            <rect x="188" y="245" width="24" height="16" rx="3" stroke="white" strokeWidth="1.5" fill="none" />
            <rect x="308" y="245" width="24" height="16" rx="3" stroke="white" strokeWidth="1.5" fill="none" />
            {/* Tank level indicator */}
            <line x1="160" y1="370" x2="360" y2="370" stroke="white" strokeWidth="1" strokeDasharray="3 2" />
            {/* Gauge on surface */}
            <circle cx="260" cy="230" r="18" stroke="white" strokeWidth="1.5" fill="none" />
            <line x1="260" y1="220" x2="260" y2="230" stroke="white" strokeWidth="1.5" />
            <line x1="260" y1="230" x2="268" y2="226" stroke="white" strokeWidth="1.5" />
            <line x1="260" y1="260" x2="260" y2="248" stroke="white" strokeWidth="1.5" />

            {/* Underground tank 2 */}
            <rect x="520" y="320" width="240" height="120" rx="60" stroke="white" strokeWidth="2.5" fill="none" />
            <rect x="533" y="333" width="214" height="94" rx="47" stroke="white" strokeWidth="1" fill="none" strokeDasharray="4 3" />
            {/* Connecting piping */}
            <path d="M 400 340 L 440 340 L 440 260 L 520 260 L 520 320" stroke="white" strokeWidth="2" fill="none" />
            <line x1="640" y1="260" x2="640" y2="320" stroke="white" strokeWidth="2" />
            {/* Sump box at surface */}
            <rect x="620" y="240" width="40" height="24" rx="3" stroke="white" strokeWidth="1.5" fill="none" />

            {/* Fuel dispenser */}
            <rect x="830" y="180" width="60" height="80" rx="5" stroke="white" strokeWidth="2" fill="none" />
            <rect x="840" y="195" width="40" height="25" rx="3" stroke="white" strokeWidth="1" fill="none" />
            <line x1="860" y1="240" x2="860" y2="260" stroke="white" strokeWidth="2" />
            {/* Dispenser hose */}
            <path d="M 870 195 Q 910 195 910 225 Q 910 245 895 245" stroke="white" strokeWidth="1.5" fill="none" />
            <circle cx="893" cy="245" r="4" stroke="white" strokeWidth="1" fill="none" />

            {/* Piping from dispenser underground to tank 2 */}
            <path d="M 860 260 L 860 380 L 760 380" stroke="white" strokeWidth="2" fill="none" />

            {/* Monitoring well */}
            <line x1="980" y1="200" x2="980" y2="450" stroke="white" strokeWidth="1.5" strokeDasharray="6 3" />
            <rect x="968" y="190" width="24" height="14" rx="3" stroke="white" strokeWidth="1.5" fill="none" />
            <circle cx="980" cy="420" r="6" stroke="white" strokeWidth="1" fill="none" />

            {/* Soil texture dots */}
            {[300, 350, 400, 450, 500].map(y =>
              [80, 180, 280, 380, 480, 580, 680, 780, 880, 1000, 1100].map(x => (
                <circle key={`${x}-${y}`} cx={x + (y % 40)} cy={y + (x % 20)} r="1" fill="white" opacity="0.5" />
              ))
            )}

            {/* Compliance check marks */}
            <g transform="translate(1050, 290)">
              <circle cx="0" cy="0" r="16" stroke="white" strokeWidth="1.5" fill="none" />
              <path d="M -6 0 L -2 4 L 7 -5" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </g>
            <g transform="translate(1050, 340)">
              <circle cx="0" cy="0" r="16" stroke="white" strokeWidth="1.5" fill="none" />
              <path d="M -6 0 L -2 4 L 7 -5" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </g>
            <g transform="translate(1050, 390)">
              <circle cx="0" cy="0" r="16" stroke="white" strokeWidth="1.5" fill="none" />
              <path d="M -6 0 L -2 4 L 7 -5" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </g>
            {/* Checklist lines */}
            <line x1="1080" y1="290" x2="1150" y2="290" stroke="white" strokeWidth="1.5" />
            <line x1="1080" y1="340" x2="1140" y2="340" stroke="white" strokeWidth="1.5" />
            <line x1="1080" y1="390" x2="1130" y2="390" stroke="white" strokeWidth="1.5" />

            {/* Cathodic protection anode */}
            <g transform="translate(440, 430)">
              <rect x="-6" y="-20" width="12" height="40" rx="2" stroke="white" strokeWidth="1.5" fill="none" />
              <line x1="0" y1="-20" x2="0" y2="-40" stroke="white" strokeWidth="1" strokeDasharray="3 2" />
            </g>
          </svg>
        </div>

        {/* Accent glow */}
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-accent rounded-full opacity-[0.04] blur-3xl"></div>

        <div className="container mx-auto px-4 relative">
          <div className="flex flex-col lg:flex-row items-center">
            <div className="lg:w-1/2 mb-12 lg:mb-0 animate-slide-up">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6 text-white">
                Diesel Storage Compliance <span className="italic font-normal text-accent">Simplified</span>
              </h1>
              <p className="text-xl mb-8 text-white opacity-90 max-w-lg">
                Full-service diesel storage tank inspection, testing, compliance management and alarm response.
              </p>
              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                <Link to="/get-quote" className="btn bg-white text-primary hover:bg-gray-100 font-semibold py-3 px-6 rounded-lg text-center shadow-soft hover:shadow-hover transition-all transform hover:-translate-y-1">
                  Request a Quote
                </Link>
                <Link to="/services" className="btn border-2 border-white text-white hover:bg-white hover:text-primary font-semibold py-3 px-6 rounded-lg transition-all text-center transform hover:-translate-y-1">
                  View Our Services
                </Link>
              </div>
            </div>

            {/* Hero SVG illustration — fuel storage system */}
            <div className="lg:w-1/2 animate-fade-in flex justify-center">
              <svg viewBox="0 0 500 420" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-lg" aria-hidden="true">
                {/* Ground surface */}
                <path d="M 20 200 L 480 200" stroke="white" strokeWidth="1.5" opacity="0.4" strokeDasharray="6 3" />
                <text x="30" y="195" fill="white" opacity="0.25" fontSize="10" fontFamily="Inter, sans-serif">GRADE LEVEL</text>

                {/* Soil cross-hatch below ground */}
                <g opacity="0.08">
                  {[220, 250, 280, 310, 340, 370].map(y => (
                    <line key={`h-${y}`} x1="20" y1={y} x2="480" y2={y} stroke="white" strokeWidth="0.5" />
                  ))}
                </g>

                {/* Underground storage tank — main */}
                <rect x="80" y="240" width="220" height="110" rx="55" stroke="white" strokeWidth="2" opacity="0.7" />
                <rect x="95" y="255" width="190" height="80" rx="40" stroke="white" strokeWidth="1" opacity="0.3" strokeDasharray="4 3" />

                {/* Liquid level inside tank */}
                <path d="M 120 305 Q 190 295 260 305" stroke="#e8751a" strokeWidth="1.5" opacity="0.5" />
                <text x="170" y="320" fill="#e8751a" opacity="0.4" fontSize="8" textAnchor="middle" fontFamily="Inter, sans-serif">FUEL LEVEL</text>

                {/* Fill pipe left */}
                <line x1="140" y1="200" x2="140" y2="240" stroke="white" strokeWidth="2" opacity="0.6" />
                <rect x="128" y="182" width="24" height="20" rx="3" stroke="white" strokeWidth="1.5" opacity="0.6" fill="none" />
                <text x="140" y="178" fill="white" opacity="0.2" fontSize="7" textAnchor="middle" fontFamily="Inter, sans-serif">FILL</text>

                {/* Vent pipe right */}
                <line x1="240" y1="200" x2="240" y2="240" stroke="white" strokeWidth="2" opacity="0.6" />
                <line x1="240" y1="148" x2="240" y2="200" stroke="white" strokeWidth="1.5" opacity="0.5" />
                <path d="M 232 148 L 248 148" stroke="white" strokeWidth="1.5" opacity="0.5" />
                <text x="255" y="170" fill="white" opacity="0.2" fontSize="7" fontFamily="Inter, sans-serif">VENT</text>

                {/* Spill bucket around fill */}
                <rect x="118" y="195" width="44" height="30" rx="4" stroke="#e8751a" strokeWidth="1.5" opacity="0.4" fill="none" />
                <text x="140" y="238" fill="#e8751a" opacity="0.3" fontSize="6" textAnchor="middle" fontFamily="Inter, sans-serif">SPILL BUCKET</text>

                {/* Piping to dispenser */}
                <path d="M 300 295 L 350 295 L 350 200 L 390 200 L 390 160" stroke="white" strokeWidth="2" opacity="0.5" fill="none" />

                {/* Fuel dispenser */}
                <rect x="370" y="100" width="50" height="62" rx="5" stroke="white" strokeWidth="2" opacity="0.7" fill="none" />
                <rect x="379" y="112" width="32" height="18" rx="2" stroke="white" strokeWidth="1" opacity="0.4" fill="none" />
                <text x="395" y="125" fill="white" opacity="0.3" fontSize="7" textAnchor="middle" fontFamily="monospace">00.00</text>
                {/* Nozzle */}
                <path d="M 408 118 Q 435 118 435 138 Q 435 150 425 150" stroke="white" strokeWidth="1.5" opacity="0.5" fill="none" />
                <circle cx="423" cy="150" r="3" stroke="white" strokeWidth="1" opacity="0.5" fill="none" />
                <rect x="365" y="160" width="60" height="8" rx="2" stroke="white" strokeWidth="1" opacity="0.4" fill="none" />
                <text x="395" y="92" fill="white" opacity="0.25" fontSize="8" textAnchor="middle" fontFamily="Inter, sans-serif">GENERATOR</text>

                {/* Sump around piping penetration */}
                <rect x="340" y="192" width="28" height="28" rx="4" stroke="#e8751a" strokeWidth="1.5" opacity="0.4" fill="none" />
                <text x="354" y="232" fill="#e8751a" opacity="0.3" fontSize="6" textAnchor="middle" fontFamily="Inter, sans-serif">SUMP</text>

                {/* Monitoring console at surface */}
                <rect x="30" y="120" width="55" height="70" rx="5" stroke="white" strokeWidth="2" opacity="0.6" fill="none" />
                <rect x="38" y="130" width="39" height="24" rx="2" stroke="white" strokeWidth="1" opacity="0.4" fill="none" />
                <line x1="42" y1="138" x2="73" y2="138" stroke="#e8751a" strokeWidth="1" opacity="0.4" />
                <line x1="42" y1="143" x2="65" y2="143" stroke="#e8751a" strokeWidth="1" opacity="0.3" />
                <line x1="42" y1="148" x2="70" y2="148" stroke="#e8751a" strokeWidth="1" opacity="0.3" />
                <circle cx="48" cy="168" r="3" stroke="#e8751a" strokeWidth="1" opacity="0.5" fill="#e8751a" fillOpacity="0.3" />
                <circle cx="58" cy="168" r="3" stroke="white" strokeWidth="1" opacity="0.3" fill="none" />
                <text x="57" y="115" fill="white" opacity="0.25" fontSize="7" textAnchor="middle" fontFamily="Inter, sans-serif">ATG MONITOR</text>
                <path d="M 57 190 L 57 200 L 57 290 L 80 290" stroke="white" strokeWidth="1" opacity="0.3" strokeDasharray="3 2" />

                {/* Compliance badges */}
                <g transform="translate(440, 250)" opacity="0.5">
                  <circle cx="0" cy="0" r="14" stroke="#e8751a" strokeWidth="1.5" fill="none" />
                  <path d="M -5 0 L -2 3 L 6 -4" stroke="#e8751a" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                </g>
                <g transform="translate(440, 290)" opacity="0.4">
                  <circle cx="0" cy="0" r="14" stroke="#e8751a" strokeWidth="1.5" fill="none" />
                  <path d="M -5 0 L -2 3 L 6 -4" stroke="#e8751a" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                </g>
                <g transform="translate(440, 330)" opacity="0.3">
                  <circle cx="0" cy="0" r="14" stroke="#e8751a" strokeWidth="1.5" fill="none" />
                  <path d="M -5 0 L -2 3 L 6 -4" stroke="#e8751a" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                </g>

                {/* Cathodic protection anode */}
                <g transform="translate(300, 370)" opacity="0.4">
                  <rect x="-5" y="-15" width="10" height="30" rx="2" stroke="white" strokeWidth="1.5" fill="none" />
                  <line x1="0" y1="-15" x2="0" y2="-30" stroke="white" strokeWidth="1" strokeDasharray="3 2" />
                  <text x="15" y="0" fill="white" opacity="0.5" fontSize="6" fontFamily="Inter, sans-serif">CP ANODE</text>
                </g>
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* Core Value Proposition */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto bg-gradient-to-br from-primary-dark to-primary rounded-2xl p-10 md:p-14 relative overflow-hidden shadow-xl">
            {/* Subtle pattern overlay */}
            <div className="absolute inset-0 opacity-[0.06]">
              <div className="absolute inset-0" style={{
                backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
                backgroundSize: '24px 24px'
              }}></div>
            </div>

            <div className="relative flex flex-col md:flex-row items-center gap-8">
              {/* Icon */}
              <div className="flex-shrink-0">
                <div className="bg-accent/20 p-5 rounded-2xl">
                  <svg className="h-16 w-16 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
              </div>

              {/* Content */}
              <div className="text-center md:text-left">
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                  We Handle Your Compliance — So You Don't Have To
                </h2>
                <p className="text-lg text-white/80 mb-0 max-w-2xl">
                  From inspections and testing to regulatory requirements and state inspector meetings, we manage all aspects of your diesel storage tank compliance. You focus on running your business — we make sure you never miss a deadline, fail an audit, or face a fine.
                </p>
              </div>

              {/* CTA */}
              <div className="flex-shrink-0">
                <Link to="/compliance-services" className="btn bg-accent text-white hover:bg-accent-dark font-semibold py-3 px-8 rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 whitespace-nowrap">
                  Learn More
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">What We Offer</h2>
            <div className="w-24 h-1 bg-accent mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive generator diesel storage tank maintenance and compliance services throughout FL, GA, SC, and NC.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <div key={service.id} className="animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                <ServiceCard service={service} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Alarm Response Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden border-l-4 border-accent">
            <div className="flex flex-col md:flex-row items-center p-8 md:p-12">
              <div className="bg-accent/10 p-4 rounded-full flex-shrink-0 mb-6 md:mb-0 md:mr-8">
                <svg className="h-12 w-12 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              </div>
              <div className="flex-grow text-center md:text-left">
                <h2 className="text-2xl font-bold mb-3">Tank Monitor Alarm Diagnostics & Resolution</h2>
                <p className="text-lg text-gray-700 mb-0 max-w-3xl">
                  Unresolved tank monitor alarms are a compliance violation that can result in fines and operational shutdowns. We troubleshoot Veeder-Root, Pneumercator, and Omntec systems to diagnose and resolve alarms. Quickly repair and replace faulty sensors and probes, getting your facility back in compliance fast.
                </p>
              </div>
              <div className="flex-shrink-0 mt-6 md:mt-0 md:ml-8">
                <Link to="/contact" className="btn bg-accent text-white hover:bg-accent-dark py-3 px-6 rounded-lg font-medium whitespace-nowrap">
                  Get Help Now
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-10 md:mb-0 md:pr-10">
              <h2 className="text-3xl font-bold mb-4">Why Choose Apex DSC?</h2>
              <div className="w-16 h-1 bg-accent mb-6"></div>
              <p className="text-lg text-gray-700 mb-6">
                With 13 years of experience in the diesel storage and generator fuel system industry, our team of professionals delivers reliable, comprehensive maintenance and compliance services that keep your business running smoothly.
              </p>

              <ul className="space-y-4">
                <li className="flex items-start bg-gray-50 p-4 rounded-lg shadow-soft transform transition-all hover:-translate-y-1 hover:shadow-hover">
                  <svg className="h-6 w-6 text-accent mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="ml-3 text-gray-700">Certified inspectors with extensive industry knowledge</span>
                </li>
                <li className="flex items-start bg-gray-50 p-4 rounded-lg shadow-soft transform transition-all hover:-translate-y-1 hover:shadow-hover">
                  <svg className="h-6 w-6 text-accent mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="ml-3 text-gray-700">Up-to-date knowledge of changing regulations</span>
                </li>
                <li className="flex items-start bg-gray-50 p-4 rounded-lg shadow-soft transform transition-all hover:-translate-y-1 hover:shadow-hover">
                  <svg className="h-6 w-6 text-accent mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="ml-3 text-gray-700">Honest, straightforward service with no hidden fees</span>
                </li>
                <li className="flex items-start bg-gray-50 p-4 rounded-lg shadow-soft transform transition-all hover:-translate-y-1 hover:shadow-hover">
                  <svg className="h-6 w-6 text-accent mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="ml-3 text-gray-700">Serving Georgia, Florida, South Carolina, and North Carolina</span>
                </li>
              </ul>

            </div>

            {/* Field photos */}
            <div className="md:w-1/2 flex justify-center">
              <div className="grid grid-cols-1 gap-4 w-full max-w-md">
                <div className="rounded-lg overflow-hidden shadow-lg relative group">
                  <img src="/images/generator-pump-room.jpg" alt="Generator fuel pump room" className="w-full h-52 object-cover brightness-[0.85] saturate-[0.75]" />
                  <div className="absolute inset-0 bg-primary/10"></div>
                </div>
                <div className="rounded-lg overflow-hidden shadow-lg relative group">
                  <img src="/images/sensor-probe-inspection.jpg" alt="Sensor and probe inspection" className="w-full h-52 object-cover brightness-[0.85] saturate-[0.75]" />
                  <div className="absolute inset-0 bg-primary/10"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Gap Stats */}
      <ComplianceGap />

      {/* CTA Section */}
      <CTASection
        title="Ready to ensure your facility's compliance?"
        subtitle="Tell us about your facility and we'll send you a custom quote within one business day."
        buttonText="Request a Quote"
        buttonLink="/get-quote"
        bgColor="dark"
      />
    </>
  )
}

export default Home
